import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useInspectionStore } from "@/stores/inspection";
import { getIN15Value, getIN15ValueFromMaterials, areThereNonControllingIN15Changes } from "@/composables/keyFields.js";

export const useKeyfieldsStore = defineStore("keyfields", () => {
  let pageData = ref({
    currentInsp: {},
    previousInsp: {},
  });
  const inspectionStore = useInspectionStore();

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getKeyfieldsObject = () => {
    return clone(pageData.value);
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    //clear idb object
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_KEYFIELDS.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((resp) => {
        convertToInspLinkObject(resp.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspLinkObject = (response) => {
    pageData.value.currentInsp = {
      // The fields in the main table
      INSPDATE: response.inspectionDate,
      INSPTYPE: response.inspectionType,
      DKRATING: response.dkRating,
      SUPRATING: response.supRating,
      SUBRATING: response.subRating,
      CULVRATING: response.culvRating,
      RAILRATING: response.railRating,
      TRANSRATIN: response.transRating,
      BEARING_OVERALL_COND_RATE: response.bearingConditionRating,
      JOINTS_OVERALL_COND_RATE: response.jointConditionRating,
      CHANRATING: response.chanRating,
      CHAN_PROT_RATING: response.chanProtRating,
      OVERALL_SCOUR_HOLE: inspectionStore.getOverallScourHole(response.scourCondRating, response.scourHole, null),
      UNDERWATER_RATING: response.underwaterRating,
      NSTM_CONDITION: response.nstmCondition,
      SCOURCRIT: response.scourCritical,
      SCOUR_CRIT_CTGRY: response.scourCriticalCat,
      STREAM_BED_MATERIAL: getIN15ValueFromMaterials(response.streamBedMaterials),
      STREAM_BED_MATERIAL_NON_CONTROLLING_CHANGE: response.streamBedMaterialNonControllingChange,
      CONDITION: response.condition,
      // 7A06 "Performed" table
      NBINSPDONE: response.nbInspectionDone,
      FCINSPDONE: response.fcInspectionDone,
      UWINSPDONE: response.uwInspectionDone,
      OSINSPDONE: response.osInspectionDone,
      ELINSPDONE: response.elInspectionDone,
      // 7A07 "Required" table
      FCINSPREQ: response.fcInspectionReq,
      UWINSPREQ: response.uwInspectionReq,
      OSINSPREQ: response.osInspectionReq,
      ELINSPREQ: response.elInspectionReq,
      // 7A09 "Interval" table
      BRINSPFREQ: response.brInspectionFreq,
      FCINSPFREQ: response.fcInspectionFreq,
      UWINSPFREQ: response.uwInspectionFreq,
      OSINSPFREQ: response.osInspectionFreq,
      ELINSPFREQ: response.elInspectionFreq,
      //SNBI fields
      ELEMREQUIRED: response.snbiFields.elemRequired,
      INDREQUIRED: response.snbiFields.indRequired,
      NSTMREQUIRED: response.snbiFields.nstmRequired,
      OSREQUIRED: response.snbiFields.osRequired,
      ROUREQUIRED: response.snbiFields.rouRequired,
      SERVREQUIRED: response.snbiFields.servRequired,
      UWREQUIRED: response.snbiFields.uwRequired,
      ELEMINTERVAL: response.snbiFields.elemInterval,
      INDINTERVAL: response.snbiFields.indInterval,
      NSTMINTERVAL: response.snbiFields.nstmInterval,
      OSINTERVAL: response.snbiFields.osInterval,
      ROUINTERVAL: response.snbiFields.rouInterval,
      SERVINTERVAL: response.snbiFields.servInterval,
      UWINTERVAL: response.snbiFields.uwInterval,
    };
    pageData.value.previousInsp = {
      INSPDATE: response.prevInspectionDate,
      INSPTYPE: response.prevInspectionType,
      DKRATING: response.prevDkRating,
      SUPRATING: response.prevSupRating,
      SUBRATING: response.prevSubRating,
      CULVRATING: response.prevCulvRating,
      RAILRATING: response.prevRailRating,
      TRANSRATIN: response.prevTransRating,
      BEARING_OVERALL_COND_RATE: response.prevBearingConditionRating,
      JOINTS_OVERALL_COND_RATE: response.prevJointConditionRating,
      CHANRATING: response.prevChanRating,
      CHAN_PROT_RATING: response.prevChanProtRating,
      OVERALL_SCOUR_HOLE: response.prevScourHole,
      UNDERWATER_RATING: response.prevUnderwaterRating,
      NSTM_CONDITION: response.prevNstmCondition,
      SCOURCRIT: response.prevScourCritical,
      SCOUR_CRIT_CTGRY: response.prevScourCriticalCat,
      STREAM_BED_MATERIAL: getIN15ValueFromMaterials(response.prevStreamBedMaterials),
      CONDITION: response.prevCondition,
      // 7A06 "Performed" table
      NBINSPDONE: response.prevNbInspectionDone,
      FCINSPDONE: response.prevFcInspectionDone,
      UWINSPDONE: response.prevUwInspectionDone,
      OSINSPDONE: response.prevOsInspectionDone,
      ELINSPDONE: response.prevElInspectionDone,
      // 7A07 "Required" table
      FCINSPREQ: response.prevFcInspectionReq,
      UWINSPREQ: response.prevUwInspectionReq,
      OSINSPREQ: response.prevOsInspectionReq,
      ELINSPREQ: response.prevElInspectionReq,
      // 7A09 "Interval" table
      BRINSPFREQ: response.prevBrInspectionFreq,
      FCINSPFREQ: response.prevFcInspectionFreq,
      UWINSPFREQ: response.prevUwInspectionFreq,
      OSINSPFREQ: response.prevOsInspectionFreq,
      ELINSPFREQ: response.prevElInspectionFreq,
      //SNBI fields
      ELEMREQUIRED: response.previousSnbiFields.elemRequired,
      INDREQUIRED: response.previousSnbiFields.indRequired,
      NSTMREQUIRED: response.previousSnbiFields.nstmRequired,
      OSREQUIRED: response.previousSnbiFields.osRequired,
      ROUREQUIRED: response.previousSnbiFields.rouRequired,
      SERVREQUIRED: response.previousSnbiFields.servRequired,
      UWREQUIRED: response.previousSnbiFields.uwRequired,
      ELEMINTERVAL: response.previousSnbiFields.elemInterval,
      INDINTERVAL: response.previousSnbiFields.indInterval,
      NSTMINTERVAL: response.previousSnbiFields.nstmInterval,
      OSINTERVAL: response.previousSnbiFields.osInterval,
      ROUINTERVAL: response.previousSnbiFields.rouInterval,
      SERVINTERVAL: response.previousSnbiFields.servInterval,
      UWINTERVAL: response.previousSnbiFields.uwInterval,
    };
  };

  const convertToInspModuleObject = (structure) => {
    const prevAcceptedInsp = structure.keyFields;
    pageData.value.currentInsp = {
      INSPDATE: structure.inspection.inspectionDate,
      INSPTYPE: structure.inspection.inspectionType,
      DKRATING: structure.inspection.dkRating,
      SUPRATING: structure.inspection.supRating,
      SUBRATING: structure.inspection.subRating,
      CULVRATING: structure.inspection.culvRating,
      RAILRATING: structure.inspection.railRating,
      TRANSRATIN: structure.inspection.transRating,
      BEARING_OVERALL_COND_RATE: structure.userInspection.overallBearingCondRating,
      JOINTS_OVERALL_COND_RATE: structure.userInspection.overallJointCondRating,
      CHANRATING: structure.inspection.chanRating,
      CHAN_PROT_RATING: structure.userInspection.chanProtRating,
      OVERALL_SCOUR_HOLE: inspectionStore.getOverallScourHole(
        structure.userInspection.scourCondRating,
        null,
        structure,
      ),
      UNDERWATER_RATING: structure.userInspection.underwaterRating,
      NSTM_CONDITION: structure.userInspection.nstmCondition,
      SCOURCRIT: structure.inspection.scourCritical,
      SCOUR_CRIT_CTGRY: structure.inspection.scourCriticalCat,
      STREAM_BED_MATERIAL: getIN15Value(structure.structureUnits, structure.inspectionsUnderwater),
      STREAM_BED_MATERIAL_NON_CONTROLLING_CHANGE: areThereNonControllingIN15Changes(
        structure.structureUnits,
        structure.inspectionsUnderwater,
        prevAcceptedInsp?.streamBedMaterials,
      ),
      CONDITION: structure.inspection.condition,
      // 7A06 "Performed" table
      NBINSPDONE: structure.inspection.nbInspectionDone,
      FCINSPDONE: structure.inspection.fcInspectionDone,
      UWINSPDONE: structure.inspection.uwInspectionDone,
      OSINSPDONE: structure.inspection.osInspectionDone,
      ELINSPDONE: structure.inspection.elInspectionDone,
      // 7A07 "Required" table
      FCINSPREQ: structure.inspection.fcInspectionReq,
      UWINSPREQ: structure.inspection.uwInspectionReq,
      OSINSPREQ: structure.inspection.osInspectionReq,
      ELINSPREQ: structure.inspection.elInspectionReq,
      // 7A09 "Interval" table
      BRINSPFREQ: structure.inspection.brInspectionFreq,
      FCINSPFREQ: structure.inspection.fcInspectionFreq,
      UWINSPFREQ: structure.inspection.uwInspectionFreq,
      OSINSPFREQ: structure.inspection.osInspectionFreq,
      ELINSPFREQ: structure.inspection.elInspectionFreq,

      //SNBI Fields
      ELEMREQUIRED: structure.snbiInspSchedule.elemRequired,
      INDREQUIRED: structure.snbiInspSchedule.indRequired,
      NSTMREQUIRED: structure.snbiInspSchedule.nstmRequired,
      OSREQUIRED: structure.snbiInspSchedule.osRequired,
      ROUREQUIRED: structure.snbiInspSchedule.rouRequired,
      SERVREQUIRED: structure.snbiInspSchedule.servRequired,
      UWREQUIRED: structure.snbiInspSchedule.uwRequired,
      ELEMINTERVAL: structure.snbiInspSchedule.elemInterval,
      INDINTERVAL: structure.snbiInspSchedule.indInterval,
      NSTMINTERVAL: structure.snbiInspSchedule.nstmInterval,
      OSINTERVAL: structure.snbiInspSchedule.osInterval,
      ROUINTERVAL: structure.snbiInspSchedule.rouInterval,
      SERVINTERVAL: structure.snbiInspSchedule.servInterval,
      UWINTERVAL: structure.snbiInspSchedule.uwInterval,
    };
    pageData.value.previousInsp = {
      INSPDATE: prevAcceptedInsp?.inspectionDate,
      INSPTYPE: prevAcceptedInsp?.inspectionType,
      DKRATING: prevAcceptedInsp?.dkRating,
      SUPRATING: prevAcceptedInsp?.supRating,
      SUBRATING: prevAcceptedInsp?.subRating,
      CULVRATING: prevAcceptedInsp?.culvRating,
      RAILRATING: prevAcceptedInsp?.railRating,
      TRANSRATIN: prevAcceptedInsp?.transRating,
      BEARING_OVERALL_COND_RATE: prevAcceptedInsp?.bearingConditionRating,
      JOINTS_OVERALL_COND_RATE: prevAcceptedInsp?.jointConditionRating,
      CHANRATING: prevAcceptedInsp?.chanRating,
      CHAN_PROT_RATING: prevAcceptedInsp?.chanProtRating,
      OVERALL_SCOUR_HOLE: inspectionStore.getOverallScourHole(
        prevAcceptedInsp?.scourCondRating,
        prevAcceptedInsp?.scourHole,
        null,
      ),
      UNDERWATER_RATING: prevAcceptedInsp?.underwaterRating,
      NSTM_CONDITION: prevAcceptedInsp?.nstmCondition,
      SCOURCRIT: prevAcceptedInsp?.scourCritical,
      SCOUR_CRIT_CTGRY: prevAcceptedInsp?.scourCriticalCat,
      STREAM_BED_MATERIAL: getIN15ValueFromMaterials(prevAcceptedInsp?.streamBedMaterials),
      CONDITION: prevAcceptedInsp?.condition,
      // 7A06 "Performed" table
      NBINSPDONE: prevAcceptedInsp?.nbInspectionDone,
      FCINSPDONE: prevAcceptedInsp?.fcInspectionDone,
      UWINSPDONE: prevAcceptedInsp?.uwInspectionDone,
      OSINSPDONE: prevAcceptedInsp?.osInspectionDone,
      ELINSPDONE: prevAcceptedInsp?.elInspectionDone,
      // 7A07 "Required" table
      FCINSPREQ: prevAcceptedInsp?.fcInspectionReq,
      UWINSPREQ: prevAcceptedInsp?.uwInspectionReq,
      OSINSPREQ: prevAcceptedInsp?.osInspectionReq,
      ELINSPREQ: prevAcceptedInsp?.elInspectionReq,
      // 7A09 "Interval" table
      BRINSPFREQ: prevAcceptedInsp?.brInspectionFreq,
      FCINSPFREQ: prevAcceptedInsp?.fcInspectionFreq,
      UWINSPFREQ: prevAcceptedInsp?.uwInspectionFreq,
      OSINSPFREQ: prevAcceptedInsp?.osInspectionFreq,
      ELINSPFREQ: prevAcceptedInsp?.elInspectionFreq,

      //SNBI Fields
      ELEMREQUIRED: prevAcceptedInsp?.previousSnbiFields?.elemRequired,
      INDREQUIRED: prevAcceptedInsp?.previousSnbiFields?.indRequired,
      NSTMREQUIRED: prevAcceptedInsp?.previousSnbiFields?.nstmRequired,
      OSREQUIRED: prevAcceptedInsp?.previousSnbiFields?.osRequired,
      ROUREQUIRED: prevAcceptedInsp?.previousSnbiFields?.rouRequired,
      SERVREQUIRED: prevAcceptedInsp?.previousSnbiFields?.servRequired,
      UWREQUIRED: prevAcceptedInsp?.previousSnbiFields?.uwRequired,
      ELEMINTERVAL: prevAcceptedInsp?.previousSnbiFields?.elemInterval,
      INDINTERVAL: prevAcceptedInsp?.previousSnbiFields?.indInterval,
      NSTMINTERVAL: prevAcceptedInsp?.previousSnbiFields?.nstmInterval,
      OSINTERVAL: prevAcceptedInsp?.previousSnbiFields?.osInterval,
      ROUINTERVAL: prevAcceptedInsp?.previousSnbiFields?.rouInterval,
      SERVINTERVAL: prevAcceptedInsp?.previousSnbiFields?.servInterval,
      UWINTERVAL: prevAcceptedInsp?.previousSnbiFields?.uwInterval,
    };
  };
  return {
    loadDataObject,
    getKeyfieldsObject,
  };
});
